import { Switch, createStyles } from '@mantine/core';
import { useCallback } from 'react';

import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';

import type { CellComponentProps, CellDescriptor } from '../../types/common';
import useAlignStyle from '../../useAlignStyle';

import dt from '~/testing';
import type { TableCellDataSwitchType, TableColumnSwitchType } from './types';

const useStyles = createStyles(() => ({
  cellWrapper: {
    display: 'flex',
  },
}));

function TableCellSwitch({
  column,
  row,
  cell,
  value,
  onChange,
}: CellComponentProps<TableColumnSwitchType, TableCellDataSwitchType>) {
  const { classes, cx } = useStyles();
  const { classes: alignClasses } = useAlignStyle();

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;
      onChange(target.checked);
    },
    [onChange]
  );

  return (
    <div
      className={cx(classes.cellWrapper, {
        [alignClasses.start]: column.params?.align === 'start',
        [alignClasses.center]: column.params?.align === 'center',
        [alignClasses.end]: column.params?.align === 'end',
      })}
    >
      <Tooltip
        isRendered={!!cell.params?.tooltip}
        withinPortal
        position="top-start"
        key={`${row.key}${column.key}`}
        label={cell.params?.tooltip}
      >
        <div>
          <Switch
            size="lg"
            key={`${row.key}${column.key}`}
            radius="xl"
            onChange={handleOnChange}
            wrapperProps={{ 'data-test-id': dt.contest.create.table.body.switch }}
            disabled={cell?.isDisabled}
            defaultChecked={cell?.defaultChecked}
            label={cell?.label}
            checked={typeof value === 'boolean' ? value : false}
          />
        </div>
      </Tooltip>
    </div>
  );
}

const descriptor: CellDescriptor<TableColumnSwitchType, TableCellDataSwitchType> = {
  name: 'Switch',
  Cell: TableCellSwitch,
};

export default descriptor;
